import { ChangeDetectorRef, Component, effect, ElementRef, inject, input, OnInit, output, signal, untracked, ViewChild } from '@angular/core';
import { ErrorMessages, LocalStorageKeyConstants, PaginationConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { AddressPipeLineSeparator, AddressPipeSecondaryAddressFormat, ModalTypes, PropertyListViewLocations } from '../../../../core/enums/base.enums';
import { PropertyMemberTypes, AddressTypes, PropertyStatuses, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { PropertyBulkActions } from '../../../../core/enums/bulk-actions.enums';
import { BulkActionRequest } from '../../../../core/models/service/bulk-action-request.model';
import { BulkActionResponse } from '../../../../core/models/service/bulk-action-response.model';
import { PropertiesFilter } from '../../../../core/filters/properties.filter';
import { PropertyListSortOptions } from '../../../../core/enums/sort-options.enums';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { PropertiesService } from '../../../../core/services/property.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { EnumUtilities } from '../../../../core/utilities/enum.utilities';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { FormState } from '../../../../core/enums/forms.enums';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { ItemSelectionPipe } from '../../../../core/pipes/item-selection/item-selection-pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { AddressModel } from '../../../../core/models/data/address.model';
import { AddressPipe } from "../../../../core/pipes/address/address.pipe";
import { NoValuePipe } from "../../../../core/pipes/no-value/no-value.pipe";
import { PropertySearchFilter } from "../property-search-filter/property-search-filter";
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { SafeHtmlPipe } from "../../../../core/pipes/safe-html/safe-html.pipe";
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PropertyFormModalComponent } from "../property-form-modal/property-form-modal.component";
import { ModalActions } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { PropertyUtilities } from '../../../../core/utilities/property.utilities';
import { PropertiesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { PropertyPermission } from '../../../../core/enums/permissions.enums';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-property-list',
  imports: [
    PageTitleComponent,
    FontAwesomeModule,
    PaginationComponent,
    FormsModule,
    CommonModule,
    InViewPortDirective,
    SortDropdownComponent,
    ItemSelectionPipe,
    AddressPipe,
    NoValuePipe,
    PropertySearchFilter,
    MobileCardCheckbox,
    SafeHtmlPipe,
    RouterLink,
    PropertyFormModalComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    IfHasPermissionDirective,
    StatusBadgeComponent,
    TooltipDirective
],
  templateUrl: './property-list.component.html',
  styleUrl: './property-list.component.scss',
  host: {"style":"display:contents"}
})
//TODO: DEV-1392 - Rename class since it's no longer a page component.
export class PropertyListPage extends BaseListPageClass implements OnInit {

  //#region Properties & Signals  
  public organization = input<OrganizationModel | undefined | null>(null);

  //Component Data
  public propertyList = signal<PropertyModel[]>([]);
  public enumUtilities = EnumUtilities;
  public addressTypes = AddressTypes;
  public AddressPipeSecondaryAddressFormat = AddressPipeSecondaryAddressFormat;
  public AddressPipeLineSeparator = AddressPipeLineSeparator;
  public propertyListViewLocations = PropertyListViewLocations;
  public currentPropertyListViewLocation = input<PropertyListViewLocations>(PropertyListViewLocations.PropertyListPage);
  public PropertyMemberTypes = PropertyMemberTypes;
  public SelectionPrimaryKeys = SelectionPrimaryKeys;
  public PropertyPermission = PropertyPermission;
  public propertyFormState = signal<FormState>(FormState.Create);
  
  //Add Member To Property Component Data
  public activeMemberId = input<number>();
  public addMemberRequested = output<{property: PropertyModel, memberType: PropertyMemberTypes}>();

  //Edit Property
  public editingProperty = signal<PropertyModel | undefined>(undefined);

  //Confirmation Modal
  public targetedProperty: PropertyModel | null = null;
  public isSubmitting = false;
  public confirmationConfirmFunction: Function = () => {};
  public confirmationCancelFunction: Function = () => {};
  private pendingStatusActivation = true;

  //Bulk Actions
  public PropertyBulkActions = PropertyBulkActions;
  public PropertyStatuses = PropertyStatuses;
  public propertyBulkAction?: PropertyBulkActions;

  // Sort
  public propertyListSortOptions = PropertyListSortOptions;

  public skeletonPropertys: PropertyModel[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  @ViewChild(PropertyFormModalComponent) propertyFormModalRef!: PropertyFormModalComponent;
  @ViewChild('propertyListInfoModalRef') propertyListInfoModalRef!: InfoModalComponent;
  @ViewChild('propertyListConfirmationModalRef') propertyListConfirmationModalRef!: ConfirmationModalComponent;

  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  // The organization this list belongs to: the profile entity's own when rendered on a member
  // profile, otherwise the active one.
  private scopedOrganizationId(): number | undefined {
    return PermissionUtilities.resolveOrganizationScope(undefined, this.profileScope, this.userSessionService).organizationId;
  }

  constructor(
    private propertiesService: PropertiesService,
    private changeDetector: ChangeDetectorRef,
    public userSessionService: UserSessionService,
    public bootstrapService: BootstrapService,
    private activatedRoute: ActivatedRoute
  ) {
    super();
    
    effect(() => {
      const org = this.userSessionService.activeOrganization();

      if (org && this.userSessionService.hasInitializedOrganization()) {
        untracked(() => this.loadProperties(false));
      }
    });
  }

  public ngOnInit(): void {
    this.createSkeletonProperties();
    this.orderBy = this.userSessionService.propertiesOrderBy;
    this.sortOptions = [...ListConstants.PROPERTIES_SORT_OPTIONS()];
    //Instead of loadPropertys being called here we are calling it from the filter control
    // then firing it's filterApplied event since we are wanting default filters on the initial page load

    this.openAddModalFromQueryParams(this.activatedRoute, '#propertyFormModal', () => this.onAddPropertyClicked());
  }

  private createSkeletonProperties(): void {
    this.skeletonPropertys = Array.from({ length: this.take }, (_, index) => ({
      propertyId: index,
      title: 'Loading...',
      lotNumber: 'loading',
      memberCount: -1,
      addresses: [
        <AddressModel>{ 
          addressId: -1, 
          addressType: AddressTypes.Mailing,
          primaryAddress: "loading", 
          secondaryAddress: "loading", 
          city: "loading", 
          stateProvince: { abbreviation: "loading", name: "loading", stateProvinceId: -1, countryId: -1 },
          primaryZip: "loading", 
          secondaryZip: "loading"
        }
      ] 
    } as PropertyModel));
  }

  //#endregion Properties & Signals
  
  //#region Data Loading
  public get displayProperties(): PropertyModel[] {
    if (this.isLoading && (!this.propertyList() || this.propertyList().length === 0)) {
      return this.skeletonPropertys;
    }
    return this.propertyList() || [];
  }

  public async loadProperties(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    this.userSessionService.propertiesFilter.memberIds = [];

    // The organization on screen, not the active one. On the member profile's Properties tab and its
    // Attach Property modal this has to be the MEMBER's organization — the modal deliberately does not
    // filter by memberId (it is picking from all of that organization's properties), so this filter is
    // the only thing scoping it, and the active organization's properties were being offered for a
    // member who belongs to a different organization (DEV-2643).
    const organizationId = this.scopedOrganizationId();
    this.userSessionService.propertiesFilter.organizationIds = organizationId ? [organizationId] : [];

    let effectiveFilter: PropertiesFilter = this.userSessionService.propertiesFilter;

    if (this.currentPropertyListViewLocation() === PropertyListViewLocations.MemberProfilePage && this.activeMemberId()) {
      this.userSessionService.propertiesFilter.memberIds.push(this.activeMemberId()!);
      // Member profile shows deactivated Properties the member owns, per DEV-1909. Build a request-
      // local copy so the shared filter — used by the main Properties list and the Attach Property
      // picker — doesn't inherit Inactive and doesn't accumulate duplicates across repeat visits.
      const sharedStatuses = this.userSessionService.propertiesFilter.statuses ?? [];
      const effectiveStatuses = sharedStatuses.includes(PropertyStatuses.Inactive)
        ? sharedStatuses
        : [...sharedStatuses, PropertyStatuses.Inactive];
      effectiveFilter = { ...this.userSessionService.propertiesFilter, statuses: effectiveStatuses };
    }

    const response: ServiceResultModel<PaginatedListModel<PropertyModel>> = await this.propertiesService.searchProperties(effectiveFilter, this.orderBy, this.skip(), this.take);
    
    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append == true) {        
          this.propertyList.set(this.propertyList().concat(response.data.data));
        } else {      
          this.propertyList.set(response.data.data);
          //Clear Selected Items
          this.isSelectAll = false;
          this.selectionItems.set([]);
        }
      }

    this.isLoading = false;
    this.calculateSelected();
    //TODO - DEV -1393 - See if we can get rid of this manual changeDetection call
    this.changeDetector.detectChanges();    
  }

  public async loadMore(): Promise<void> {
    this.loadProperties(true);
  }

  public filterApplied(): void {
    this.loadProperties();
  }

  public loadMoreInView(): void {       
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  
  public propertyFormSuccessConfirmed(): void {      
      this.loadProperties();
  }

  public clearSearch(): void {
    this.userSessionService.propertiesFilter.searchTerm = "";
  }
  //#endregion Data Loading

  //#region Sorting & Pagination

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [PropertyListSortOptions, PropertyListSortOptions]> = {
      "Address": [PropertyListSortOptions.AddressASC, PropertyListSortOptions.AddressDESC],
      "Title": [PropertyListSortOptions.TitleASC, PropertyListSortOptions.TitleDESC],
      "Lot Number": [PropertyListSortOptions.LotNumberASC, PropertyListSortOptions.LotNumberDESC],
      "Member Count": [PropertyListSortOptions.MemberCountASC, PropertyListSortOptions.MemberCountDESC],
      "Status": [PropertyListSortOptions.StatusASC, PropertyListSortOptions.StatusDESC],
    };
    
    const [ascOption, descOption] = columnSortMap[columnName]
    // fall back to default if columnName isn't present
    || [PropertyListSortOptions.AddressASC, PropertyListSortOptions.AddressDESC];
    
    let sortDirection: PropertyListSortOptions;
    
    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }
    
    this.setSortOrder(sortDirection);
  }
  
  public setSortOrder(organizationListSortOption: string) {
    this.orderBy = organizationListSortOption;
    this.userSessionService.propertiesOrderBy = this.orderBy;
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.PROPERTIES_ORDERBY(), this.orderBy);
    this.filterApplied();
  }

  public jumpToTop(element: string) : void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }
  
  //#endregion Sorting & Pagination

  //#region Actions

  public onEditPropertyClicked(property: PropertyModel): void {
    this.editingProperty.set(property);
    this.propertyFormState.set(FormState.Update);
    this.propertyFormModalRef.openPropertyModal();
  }

  public onAddPropertyClicked(): void {
    this.editingProperty.set(undefined);
    this.propertyFormState.set(FormState.Create);
    this.propertyFormModalRef.openPropertyModal();
  }

  public onAddMemberToPropertyClicked(property: PropertyModel, propertyMemberType: PropertyMemberTypes): void {
    this.addMemberRequested.emit({
      property: property,
      memberType: propertyMemberType
    });
  }

  //#endregion Actions

  //#region Delete Property

  public confirmPropertyDeletion(property: PropertyModel): void {
    this.setDeletionConfirmationModalFunctions();
    this.targetedProperty = property;

    const confirmationMessageData = {propertyLabel: PropertyUtilities.getPropertyLabel(property)};
    const confirmationMessage = PropertiesMessagingConstants.PROPERTIES_DELETE_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

    this.propertyListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.propertyListConfirmationModalRef.message.set(confirmationMessage);
    this.propertyListConfirmationModalRef.enableNotes.set(true);
    this.propertyListConfirmationModalRef.isNoteRequired.set(false);
    this.propertyListConfirmationModalRef.enableTypeToConfirm.set(false);

    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Show);
  }

  public setDeletionConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.confirmedPropertyDeletionConfirmation;
    this.confirmationCancelFunction = this.cancelPropertyDeletionConfirmation;
  }

  public async confirmedPropertyDeletionConfirmation(note: string | undefined): Promise<void> {
    this.isSubmitting = true;
    const response = await this.propertiesService.deleteProperty(this.targetedProperty!.propertyId, note);

    if (response.success === false) {
      this.propertyListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      this.propertyListInfoModalRef.modalType.set(ModalTypes.Success);
      this.selectionItems.set([]);
      this.loadProperties();
    }

    this.isSubmitting = false;
    this.propertyListInfoModalRef.message.set(response.message);
    this.bootstrapService.manageModal("#propertyListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Hide);
  }

  public cancelPropertyDeletionConfirmation(): void {
    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Hide);
  }

  //#endregion Delete Property

  //#region Activate / Deactivate

  public confirmPropertyStatusChange(property: PropertyModel, activate: boolean): void {
    this.setStatusChangeConfirmationModalFunctions();
    this.targetedProperty = property;
    this.pendingStatusActivation = activate;

    const confirmationMessage = PropertiesMessagingConstants.PROPERTIES_UPDATE_STATUS_CONFIRMATION().replaceValuesWithObject({
      actionText: activate ? 'reactivate' : 'deactivate',
      propertyLabel: PropertyUtilities.getPropertyLabel(property)
    });

    this.propertyListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.propertyListConfirmationModalRef.message.set(confirmationMessage);
    this.propertyListConfirmationModalRef.enableNotes.set(true);
    this.propertyListConfirmationModalRef.isNoteRequired.set(false);
    this.propertyListConfirmationModalRef.enableTypeToConfirm.set(false);

    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Show);
  }

  public setStatusChangeConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.confirmedStatusChangeConfirmation;
    this.confirmationCancelFunction = this.cancelPropertyDeletionConfirmation;
  }

  public async confirmedStatusChangeConfirmation(note: string | undefined): Promise<void> {
    // Shared confirmation modal shows a spinner but does not disable the Yes button — guard against a
    // fast double-click firing two mutations. Mirrors the property profile (DEV-1909, commit 600bef0d).
    if (this.isSubmitting) return;
    if (this.targetedProperty === null) return;

    this.isSubmitting = true;
    try {
      const response = await this.propertiesService.updatePropertyStatus(
        this.targetedProperty.propertyId,
        this.pendingStatusActivation,
        note
      );

      if (response.success === false) {
        this.propertyListInfoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        this.propertyListInfoModalRef.modalType.set(ModalTypes.Success);
        this.loadProperties();
      }

      this.propertyListInfoModalRef.message.set(response.message);
      this.bootstrapService.manageModal("#propertyListInfoModal", ModalActions.Show);
      this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  //#endregion Activate / Deactivate

  //#region Bulk Actions

  public confirmBulkAction(bulkAction: PropertyBulkActions): void {
    this.setBulkActionConfirmationModalFunctions();
    this.propertyBulkAction = bulkAction;

    this.propertyListConfirmationModalRef.enableNotes.set(true);
    this.propertyListConfirmationModalRef.isNoteRequired.set(false);
    this.propertyListConfirmationModalRef.enableTypeToConfirm.set(true);
    this.propertyListConfirmationModalRef.modalType.set(ModalTypes.Question);

    let typeToConfirmText: string;
    let confirmationMessage: string;

    switch (bulkAction) {
      case PropertyBulkActions.Activate:
        typeToConfirmText = PropertiesMessagingConstants.PROPERTIES_BULK_REACTIVATE_TYPE_TO_CONFIRM();
        confirmationMessage = PropertiesMessagingConstants.PROPERTIES_BULK_STATUS_CONFIRMATION()
          .replaceValuesWithObject({actionText: 'reactivate', selectedCount: this.selectedCount()});
        break;
      case PropertyBulkActions.Deactivate:
        typeToConfirmText = PropertiesMessagingConstants.PROPERTIES_BULK_DEACTIVATE_TYPE_TO_CONFIRM();
        confirmationMessage = PropertiesMessagingConstants.PROPERTIES_BULK_STATUS_CONFIRMATION()
          .replaceValuesWithObject({actionText: 'deactivate', selectedCount: this.selectedCount()});
        break;
      case PropertyBulkActions.Delete:
      default:
        typeToConfirmText = 'DELETE PROPERTIES';
        confirmationMessage = PropertiesMessagingConstants.PROPERTIES_BULK_DELETE_CONFIRMATION()
          .replaceValuesWithObject({selectedCount: this.selectedCount()});
        break;
    }

    this.propertyListConfirmationModalRef.typeToConfirmText.set(typeToConfirmText);
    this.propertyListConfirmationModalRef.message.set(confirmationMessage);
    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Show);
  }

  public setBulkActionConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.bulkActionModalConfirmed;
    this.confirmationCancelFunction = this.bulkActionModalCancelled;
  }

  public async bulkActionModalConfirmed(note: string | undefined): Promise<void> {
    this.isSubmitting = true;
    let bulkActionRequest: BulkActionRequest<PropertiesFilter | null> = {
      useFilter: this.isSelectAll,
      filter: (this.isSelectAll === true) ? this.userSessionService.propertiesFilter : null,
      selectedIds: (this.isSelectAll === false) ? this.selectionItems() : null,
      excludedIds: (this.isSelectAll === true) ? this.selectionItems() : null,
      note: note ?? null
    };

    let response: ServiceResultModel<BulkActionResponse>;
    switch (this.propertyBulkAction) {
      case PropertyBulkActions.Activate:
      case PropertyBulkActions.Deactivate:
        response = await this.propertiesService.bulkUpdatePropertyStatus(this.propertyBulkAction, bulkActionRequest);
        break;
      case PropertyBulkActions.Delete:
        response = await this.propertiesService.bulkDeleteProperties(bulkActionRequest);
        break;
      default:
        response = {success: false, message: ErrorMessages.DEFAULT_FAILURE_MESSAGE()};
    }

    if (response.success === false) {
      this.propertyListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      this.propertyListInfoModalRef.modalType.set(ModalTypes.Success);
      this.selectionItems.set([]);
      this.loadProperties();
    }

    this.isSubmitting = false;
    this.propertyListInfoModalRef.message.set(response.message);
    this.bootstrapService.manageModal("#propertyListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Hide);
  }

  public bulkActionModalCancelled(): void {
    this.bootstrapService.manageModal("#propertyListConfirmationModal", ModalActions.Hide);
  }

  //#endregion Bulk Actions
}
